// src/components/admin/EndWeekReport.jsx
import React, { useState } from "react";
import { collection, query, where, getDocs, Timestamp, doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function EndWeekReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Cache for user data to avoid repeated lookups
  const [userCache, setUserCache] = useState({});

  // Helper function to get user data from cache or Firebase
  const getUserData = async (userId) => {
    if (!userId) return { name: "Unknown User", role: "unknown", email: "" };

    if (userCache[userId]) {
      return userCache[userId];
    }

    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userInfo = {
          name: userData.name || userData.displayName || "Unknown User",
          role: userData.role.toLowerCase() || "unknown",
          email: userData.email || ""
        };
        setUserCache(prev => ({ ...prev, [userId]: userInfo }));
        return userInfo;
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }

    return { name: "Unknown User", role: "unknown", email: "" };
  };

  // Helper: normalize audit log document
  const normalizeAuditLog = async (docSnap) => {
    const d = docSnap.data() || {};
    const date = d.timestamp?.toDate ? d.timestamp.toDate().toLocaleString() : "—";

    let user, role, email;
    if (!d.userId) {
      // For actions without user, default to administrator
      user = "Administrator";
      role = "admin";
      email = "admin@system.com";
    } else {
      // Use logged data for name, but current role and email from user data
      const userData = await getUserData(d.userId);
      user = d.userName || userData.name;
      role = userData.role;
      email = userData.email;
    }

    // Extract action details
    const action = d.action || "Unknown Action";
    const details = d.message || "No additional details";

    return {
      date,
      user,
      role,
      email,
      action,
      details,
      type: "audit"
    };
  };

  // Helper: normalize request document
  const normalizeRequest = async (docSnap) => {
    const d = docSnap.data() || {};
    if (!d.createdAt) return null;

    const date = d.createdAt.toDate().toLocaleString();

    // Get requester information
    const userData = await getUserData(d.requesterId);

    const action = `${d.type === 'maintenance' ? 'Maintenance' : 'Borrow'} Request Submitted`;

    const details = [
      `Asset: ${d.assetName || 'Unknown'}`,
      `Category: ${d.assetCategory || 'Unknown'}`,
      `Location: ${d.currentLocation || 'Unknown'}`,
      `Status: ${d.status || 'Unknown'}`,
      `Urgency: ${d.urgency || 'Normal'}`,
      d.reason ? `Reason: ${d.reason}` : null,
      d.assignedToName ? `Assigned To: ${d.assignedToName}` : null
    ].filter(Boolean).join(" | ");

    return {
      date,
      user: userData.name,
      role: userData.role,
      email: userData.email,
      action,
      details,
      type: "request"
    };
  };

  // Helper: normalize maintenance task document
  const normalizeMaintenanceTask = async (docSnap) => {
    const d = docSnap.data() || {};
    const date = d.createdAt?.toDate ? d.createdAt.toDate().toLocaleString() : "—";

    // Get technician information
    const technicianData = await getUserData(d.assignedTechnician);

    // Get technician notes from the associated request
    let technicianNotes = null;
    if (d.requestId) {
      try {
        const requestDoc = await getDoc(doc(db, "requests", d.requestId));
        if (requestDoc.exists()) {
          const requestData = requestDoc.data();
          technicianNotes = requestData.technicianNotes;
        }
      } catch (error) {
        console.error("Error fetching technician notes from request:", error);
      }
    }

    const action = "Completed Maintenance Task";

    const detailsArray = [
      `Asset: ${d.assetName || 'Unknown'}`,
      `Description: ${d.description || 'No description'}`,
      `Priority: ${d.priority || 'Normal'}`,
      `Location: ${d.location || 'TBD'}`,
      `Technician: ${d.technicianName || technicianData.name}`,
      `Requester: ${d.requesterName || 'Unknown'}`
    ];

    if (technicianNotes) {
      detailsArray.push(`Technician Notes: ${technicianNotes}`);
    }

    const details = detailsArray.filter(Boolean).join(" | ");

    return {
      date,
      user: technicianData.name,
      role: technicianData.role,
      email: technicianData.email,
      action,
      details,
      type: "maintenance_task"
    };
  };

  // Helper: normalize added user document
  const normalizeAddedUser = async (docSnap) => {
    const d = docSnap.data() || {};
    if (!d.createdAt) return null;

    const date = d.createdAt.toDate().toLocaleString();

    // Get admin information who added the user
    const adminData = await getUserData(d.createdBy || "unknown");

    const action = "Added user";

    const details = [
      `Name: ${d.name || 'Unknown'}`,
      `Email: ${d.email || 'Unknown'}`,
      `Role: ${d.role || 'Unknown'}`
    ].filter(Boolean).join(" | ");

    return {
      date,
      user: adminData.name,
      role: adminData.role,
      email: adminData.email,
      action,
      details,
      type: "added_user"
    };
  };





  // Fetch logs and requests between startDate and endDate inclusive
  const fetchWeeklyData = async () => {
    if (!startDate || !endDate) {
      alert("Please select both a start and end date.");
      return;
    }

    // ensure start <= end
    if (new Date(startDate) > new Date(endDate)) {
      alert("Start date cannot be after end date.");
      return;
    }

    try {
      setLoading(true);
      // Firestore Timestamp: start of startDate, end-of-day for endDate
      const start = Timestamp.fromDate(new Date(startDate + "T00:00:00"));
      const end = Timestamp.fromDate(new Date(endDate + "T23:59:59"));

      // Fetch audit logs
      const auditQuery = query(
        collection(db, "auditLogs"),
        where("timestamp", ">=", start),
        where("timestamp", "<=", end)
      );

      // Fetch requests
      const requestQuery = query(
        collection(db, "requests"),
        where("createdAt", ">=", start),
        where("createdAt", "<=", end)
      );

      // Fetch maintenance tasks
      const maintenanceTaskQuery = query(
        collection(db, "maintenanceTasks"),
        where("createdAt", ">=", start),
        where("createdAt", "<=", end)
      );

      // Fetch added users
      const userQuery = query(
        collection(db, "users"),
        where("createdAt", ">=", start),
        where("createdAt", "<=", end)
      );

      const [auditSnap, requestSnap, taskSnap, userSnap] = await Promise.all([
        getDocs(auditQuery),
        getDocs(requestQuery),
        getDocs(maintenanceTaskQuery),
        getDocs(userQuery)
      ]);

      // Process audit logs
      const auditRows = await Promise.all(
        auditSnap.docs.map((docSnap) => normalizeAuditLog(docSnap))
      ).then(rows => rows.filter(row => row !== null));

      // Process requests
      const requestRows = await Promise.all(
        requestSnap.docs.map((docSnap) => normalizeRequest(docSnap))
      ).then(rows => rows.filter(row => row !== null));

      // Process maintenance tasks - filter for completed status
      const completedTaskDocs = taskSnap.docs.filter(doc => doc.data().status === 'completed');
      const taskRows = await Promise.all(
        completedTaskDocs.map((docSnap) => normalizeMaintenanceTask(docSnap))
      );

      // Process added users
      const userRows = await Promise.all(
        userSnap.docs.map((docSnap) => normalizeAddedUser(docSnap))
      ).then(rows => rows.filter(row => row !== null));

      // Combine and sort all data by date
      const allRows = [...auditRows, ...requestRows, ...taskRows, ...userRows];
      allRows.sort((a, b) => {
        const da = a.date && a.date !== "—" ? new Date(a.date).getTime() : 0;
        const db = b.date && b.date !== "—" ? new Date(b.date).getTime() : 0;
        return da - db;
      });

      setData(allRows);
    } catch (err) {
      console.error("Error fetching weekly data:", err);
      alert("❌ Error fetching data: " + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Export CSV
  const exportCSV = () => {
    if (data.length === 0) return alert("No data to export.");

    // Transform data for better Excel viewing
    const csvData = data.map(row => {
      // Parse details string and extract key information
      const details = row.details || "";
      const detailParts = details.split(" | ");

      // Extract specific fields from details
      const extractedData = {
        Date: row.date,
        User: row.user,
        Role: row.role,
        Email: row.email || "",
        Action: row.action,
        // Initialize all possible columns
        "Asset Name": "",
        "Asset ID": "",
        Category: "",
        Location: "",
        Status: "",
        Urgency: "",
        Priority: "",
        Reason: "",
        "Assigned To": "",
        "Requested By": "",
        "Technician": "",
        "Requester": "",
        "Task ID": "",
        Note: "",
        Description: "",
        "Added User Name": "",
        "Added User Email": "",
        "Added User Role": ""
      };

      // Parse detail parts and populate columns
      detailParts.forEach(part => {
        if (part.includes(": ")) {
          const [key, value] = part.split(": ", 2);
          switch (key.trim()) {
            case "Asset":
              extractedData["Asset Name"] = value;
              break;
            case "Asset ID":
              extractedData["Asset ID"] = value;
              break;
            case "Category":
              extractedData.Category = value;
              break;
            case "Location":
              extractedData.Location = value;
              break;
            case "Status":
              extractedData.Status = value;
              break;
            case "Urgency":
              extractedData.Urgency = value;
              break;
            case "Priority":
              extractedData.Priority = value;
              break;
            case "Reason":
              extractedData.Reason = value;
              break;
            case "Assigned To":
              extractedData["Assigned To"] = value;
              break;
            case "Requested By":
              extractedData["Requested By"] = value;
              break;
            case "Technician":
              extractedData["Technician"] = value;
              break;
            case "Requester":
              extractedData["Requester"] = value;
              break;
            case "Task ID":
              extractedData["Task ID"] = value;
              break;
            case "Note":
              extractedData.Note = value;
              break;
            case "Technician Notes":
              extractedData.Note = value; // Map technician notes to the Note column
              break;
            case "Description":
              extractedData.Description = value;
              break;
            case "Name":
              extractedData["Added User Name"] = value;
              break;
            case "Email":
              extractedData["Added User Email"] = value;
              break;
            case "Role":
              extractedData["Added User Role"] = value;
              break;
            default:
              // If no specific column, add to a general details column
              if (!extractedData["Additional Details"]) {
                extractedData["Additional Details"] = part;
              } else {
                extractedData["Additional Details"] += "; " + part;
              }
          }
        }
      });

      return extractedData;
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeStart = startDate || "start";
    const safeEnd = endDate || "end";
    link.download = `weekly_report_${safeStart}_to_${safeEnd}.csv`;
    link.click();
  };

  // Export PDF using jsPDF + autotable
  const exportPDF = () => {
    if (data.length === 0) return alert("No data to export.");

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    const logoUrl = "/school-logo.png"; // optional: place logo in /public
    const img = new Image();
    img.src = logoUrl;

    // Draw content after logo loads (or immediately if logo not found)
    img.onload = () => drawPDF(doc, img);
    img.onerror = () => drawPDF(doc, null);

    function drawPDF(docInstance, logoImage) {
      const dateStr = new Date().toLocaleString();
      // Add logo top-left if available
      if (logoImage) {
        try {
          docInstance.addImage(logoImage, "PNG", 14, 8, 20, 20);
        } catch (e) {
          console.warn("Failed to add logo image to PDF:", e);
        }
      }

      // Title and meta
      docInstance.setFontSize(14);
      docInstance.text("CATHS Asset Management System", 105, 15, { align: "center" });
      docInstance.setFontSize(12);
      docInstance.text("End of Week Report", 105, 22, { align: "center" });
      docInstance.setFontSize(10);
      docInstance.text(`Generated: ${dateStr}`, 105, 28, { align: "center" });
      const periodText = startDate && endDate ? `Period: ${startDate} to ${endDate}` : "Period: —";
      docInstance.text(periodText, 105, 33, { align: "center" });

      // Prepare table body
      const tableBody = data.map((r) => [r.date, r.user, r.role, r.action, r.details]);

      // call autoTable (imported as function)
      autoTable(docInstance, {
        startY: 40,
        head: [["Date", "User", "Role", "Action", "Details"]],
        body: tableBody,
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
        headStyles: { fillColor: [41, 128, 185] },
        columnStyles: {
          0: { cellWidth: 30 }, // Date
          1: { cellWidth: 30 }, // User
          2: { cellWidth: 20 }, // Role
          3: { cellWidth: 30 }, // Action
          4: { cellWidth: 80 }, // Details (wrap)
        },
        theme: "striped",
      });

      docInstance.lastAutoTable = {};

      // footer / totals: guard against undefined lastAutoTable
      const finalY = docInstance.lastAutoTable?.finalY ?? docInstance.previousAutoTable?.finalY ?? 40;
      const total = data.length;
      docInstance.setFontSize(10);
      docInstance.text(`Total Actions: ${total}`, 14, finalY + 10);

      const safeStart = startDate || "start";
      const safeEnd = endDate || "end";
      docInstance.save(`weekly_report_${safeStart}_to_${safeEnd}.pdf`);
    }
  };

  // Filter data based on search term
  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      row.date.toLowerCase().includes(term) ||
      row.user.toLowerCase().includes(term) ||
      row.role.toLowerCase().includes(term) ||
      row.action.toLowerCase().includes(term) ||
      row.details.toLowerCase().includes(term)
    );
  });

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
        📅 End of Week Reporting
      </h1>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label className="text-sm text-gray-700 dark:text-gray-300 block">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 px-2 py-1 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />
        </div>

        <div>
          <label className="text-sm text-gray-700 dark:text-gray-300 block">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 px-2 py-1 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchWeeklyData}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Generate Report"}
          </button>

          {data.length > 0 && (
            <>
              <button
                onClick={exportCSV}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Export CSV
              </button>
              <button
                onClick={exportPDF}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Export PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search Bar */}
      {data.length > 0 && (
        <>
          <div className="mb-4">
            <label className="text-sm text-gray-700 dark:text-gray-300 block">Search Records</label>
            <input
              type="text"
              placeholder="Search by date, user, role, action, or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-1 px-3 py-2 border border-gray-300 rounded w-full max-w-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
          <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredData.length} of {data.length} records
          </p>
        </>
      )}

      {/* Status */}
      {loading && <p className="text-gray-500">Fetching audit data...</p>}

      {/* Table */}
      {filteredData.length > 0 ? (
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full border border-gray-300 text-sm">
            <thead className="bg-gray-200 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 border">Date</th>
                <th className="px-4 py-2 border">User</th>
                <th className="px-4 py-2 border">Role</th>
                <th className="px-4 py-2 border">Action</th>
                <th className="px-4 py-2 border">Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((r, i) => (
                <tr key={i} className="hover:bg-gray-100 dark:hover:bg-gray-800 align-top">
                  <td className="px-4 py-2 border align-top whitespace-normal">{r.date}</td>
                  <td className="px-4 py-2 border align-top whitespace-normal">{r.user}</td>
                  <td className="px-4 py-2 border align-top whitespace-normal">{r.role}</td>
                  <td className="px-4 py-2 border align-top whitespace-normal">{r.action}</td>
                  <td className="px-4 py-2 border align-top whitespace-normal">{r.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <p className="mt-4 text-gray-500">
            {data.length === 0
              ? "No records found for the selected dates. Please choose a period and click Generate Report."
              : "No records match the search term. Please try a different search."
            }
          </p>
        )
      )}
    </div>
  );
}
